/**
 * ToolBlockPopover Component (Task Group 5)
 *
 * Provides a click-triggered popover for inline parameter editing of tool blocks.
 *
 * Features:
 * - Radix Popover triggered on click
 * - X button to close and apply changes
 * - Duration input (all tools)
 * - Target Rotation input (movement tools only)
 * - Easing dropdown (movement tools only)
 * - Immediate updates via onChange/onBlur
 * - Dark theme styling consistent with app
 *
 * Control tools (WAIT, STOP) only display the duration field since they don't
 * have rotation or easing parameters.
 */

import {
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
  type ReactNode,
} from 'react';
import * as Popover from '@radix-ui/react-popover';
import { X } from 'lucide-react';
import type {
  Tool,
  BodyPart,
  ResetTargetPosition,
} from '../../stores/timelineStore';
import type { EasingFunction } from '../../stores/puppetStore';
import { formatDuration } from './utils/formatDuration';
import { isResetPositionTool } from '../../utils/resetPositionTool';

/**
 * Minimum duration in seconds for tool blocks
 * Same as MIN_DURATION in useResizeHandles
 */
const MIN_DURATION = 0.1;

/**
 * Maximum duration in seconds (reasonable upper limit)
 */
const MAX_DURATION = 30;

/**
 * Available easing options for movement tools
 */
const EASING_OPTIONS: { value: EasingFunction; label: string }[] = [
  { value: 'linear', label: 'Linear' },
  { value: 'ease-in', label: 'Ease In' },
  { value: 'ease-out', label: 'Ease Out' },
  { value: 'ease-in-out', label: 'Ease In-Out' },
];

/**
 * Target position options for Reset Position tool
 */
const TARGET_POSITION_OPTIONS: { value: ResetTargetPosition; label: string }[] =
  [
    { value: 'start', label: 'Starting Position' },
    { value: 'dock', label: 'Docking Position' },
  ];

interface ToolBlockPopoverProps {
  /**
   * Tool data from timeline store
   */
  tool: Tool;

  /**
   * Callback when tool parameters are updated
   */
  onUpdate: (updates: Partial<Tool>) => void;

  /**
   * Whether the popover is open (controlled mode)
   */
  isOpen: boolean;

  /**
   * Callback when open state changes
   */
  onOpenChange: (open: boolean) => void;

  /**
   * Trigger element (the ToolBlock content)
   */
  children: ReactNode;

  /**
   * Whether popover should be disabled (e.g., during resize)
   */
  disabled?: boolean;

  /**
   * Cursor position for positioning the popover (optional)
   * If provided, popover appears near the cursor instead of centered on trigger
   */
  cursorPosition?: { x: number; y: number } | null;
}

/**
 * Check if a body part is a control type (WAIT/STOP)
 * Control tools only show duration, not rotation or easing
 */
function isControlBodyPart(bodyPart: BodyPart): boolean {
  return bodyPart === 'control';
}

/**
 * ToolBlockPopover component for inline parameter editing
 *
 * @example
 * ```tsx
 * <ToolBlockPopover
 *   tool={tool}
 *   onUpdate={(updates) => timelineStore.updateTool(tool.id, updates)}
 *   isOpen={isPopoverOpen}
 *   onOpenChange={setPopoverOpen}
 * >
 *   <ToolBlockContent />
 * </ToolBlockPopover>
 * ```
 */
export function ToolBlockPopover({
  tool,
  onUpdate,
  isOpen,
  onOpenChange,
  children,
  disabled = false,
  cursorPosition = null,
}: ToolBlockPopoverProps) {
  // Derive display values directly from tool props
  // This avoids the need for useEffect with setState
  const derivedDurationValue = useMemo(
    () => formatDuration(tool.duration),
    [tool.duration]
  );
  const derivedRotationValue = useMemo(
    () => tool.targetRotation.toString(),
    [tool.targetRotation]
  );

  // Local editing state - only used while actively editing
  const [editingDuration, setEditingDuration] = useState<string | null>(null);
  const [editingRotation, setEditingRotation] = useState<string | null>(null);

  // Refs to track current editing values for onOpenChange callback
  const editingDurationRef = useRef<string | null>(null);
  const editingRotationRef = useRef<string | null>(null);

  // Keep refs in sync with state (via effect to satisfy react-hooks/refs)
  useEffect(() => {
    editingDurationRef.current = editingDuration;
    editingRotationRef.current = editingRotation;
  }, [editingDuration, editingRotation]);

  // Use editing value if actively editing, otherwise use derived value
  const durationValue = editingDuration ?? derivedDurationValue;
  const rotationValue = editingRotation ?? derivedRotationValue;

  // Check if this is a control tool (no rotation/easing fields)
  const isControl = isControlBodyPart(tool.bodyPart);

  // Check if this is a Reset Position tool (shows targetPosition dropdown)
  const isResetPosition = isResetPositionTool(tool.toolId);

  /**
   * Handle duration change - track editing state
   */
  const handleDurationChange = useCallback((value: string) => {
    setEditingDuration(value);
  }, []);

  /**
   * Handle duration blur - validate, apply, and clear editing state
   */
  const handleDurationBlur = useCallback(() => {
    if (editingDuration === null) return;

    const parsed = parseFloat(editingDuration);

    // Clear editing state
    setEditingDuration(null);

    if (isNaN(parsed)) {
      // Invalid input - just clear editing state (will revert to derived value)
      return;
    }

    // Clamp to valid range
    const clamped = Math.max(MIN_DURATION, Math.min(MAX_DURATION, parsed));

    // Only update if value actually changed
    if (clamped !== tool.duration) {
      onUpdate({ duration: clamped });
    }
  }, [editingDuration, tool.duration, onUpdate]);

  /**
   * Handle target rotation change - track editing state
   */
  const handleRotationChange = useCallback((value: string) => {
    setEditingRotation(value);
  }, []);

  /**
   * Handle rotation blur - validate, apply, and clear editing state
   */
  const handleRotationBlur = useCallback(() => {
    if (editingRotation === null) return;

    const parsed = parseInt(editingRotation, 10);

    // Clear editing state
    setEditingRotation(null);

    if (isNaN(parsed)) {
      // Invalid input - just clear editing state (will revert to derived value)
      return;
    }

    // Only update if value actually changed
    if (parsed !== tool.targetRotation) {
      onUpdate({ targetRotation: parsed });
    }
  }, [editingRotation, tool.targetRotation, onUpdate]);

  /**
   * Handle easing change
   * Updates immediately on selection change
   */
  const handleEasingChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const value = event.target.value as EasingFunction;
      if (value !== tool.easing) {
        onUpdate({ easing: value });
      }
    },
    [tool.easing, onUpdate]
  );

  /**
   * Handle target position change (Reset Position tool only)
   * Updates immediately on selection change
   */
  const handleTargetPositionChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const value = event.target.value as ResetTargetPosition;
      if (value !== tool.targetPosition) {
        onUpdate({ targetPosition: value });
      }
    },
    [tool.targetPosition, onUpdate]
  );

  /**
   * Apply any pending edits before closing
   * This ensures edits are saved even when clicking outside the popover
   */
  const applyPendingEdits = useCallback(() => {
    const updates: Partial<Tool> = {};

    // Check for pending duration edit
    if (editingDurationRef.current !== null) {
      const parsed = parseFloat(editingDurationRef.current);
      if (!isNaN(parsed)) {
        const clamped = Math.max(MIN_DURATION, Math.min(MAX_DURATION, parsed));
        if (clamped !== tool.duration) {
          updates.duration = clamped;
        }
      }
      setEditingDuration(null);
    }

    // Check for pending rotation edit
    if (editingRotationRef.current !== null) {
      const parsed = parseInt(editingRotationRef.current, 10);
      if (!isNaN(parsed) && parsed !== tool.targetRotation) {
        updates.targetRotation = parsed;
      }
      setEditingRotation(null);
    }

    // Apply updates if any
    if (Object.keys(updates).length > 0) {
      onUpdate(updates);
    }
  }, [tool.duration, tool.targetRotation, onUpdate]);

  /**
   * Handle open state change
   * Apply pending edits when closing
   */
  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        // Apply any pending edits before closing
        applyPendingEdits();
      }
      onOpenChange(open);
    },
    [onOpenChange, applyPendingEdits]
  );

  // If disabled, just render children without popover
  if (disabled) {
    return <>{children}</>;
  }

  // Create virtual anchor rect for cursor-based positioning
  const virtualAnchor = cursorPosition
    ? {
        getBoundingClientRect: () => ({
          x: cursorPosition.x,
          y: cursorPosition.y,
          top: cursorPosition.y,
          left: cursorPosition.x,
          bottom: cursorPosition.y,
          right: cursorPosition.x,
          width: 0,
          height: 0,
          toJSON: () => ({}),
        }),
      }
    : undefined;

  return (
    <Popover.Root open={isOpen} onOpenChange={handleOpenChange}>
      {/* Use virtual anchor for cursor positioning, otherwise use trigger */}
      {cursorPosition ? (
        <Popover.Anchor virtualRef={{ current: virtualAnchor }} />
      ) : null}
      <Popover.Trigger asChild>{children}</Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          side="top"
          sideOffset={12}
          align="start"
          collisionPadding={10}
          className="z-50 bg-dark-bg border border-dark-border rounded-lg shadow-lg p-3 min-w-[200px]"
          style={{ boxShadow: 'var(--shadow-lg)' }}
        >
          {/* Close button */}
          <Popover.Close
            className="absolute top-2 right-2 p-1 rounded hover:bg-dark-surface text-dark-text-muted hover:text-dark-text transition-colors"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </Popover.Close>

          <div className="space-y-3 pr-6">
            {/* Duration Input - Always Shown */}
            <div className="flex flex-col gap-1">
              <label
                htmlFor={`duration-${tool.id}`}
                className="text-xs font-medium text-dark-text-muted"
              >
                Duration (s)
              </label>
              <input
                id={`duration-${tool.id}`}
                type="number"
                step="0.1"
                min={MIN_DURATION}
                max={MAX_DURATION}
                value={durationValue}
                onChange={(e) => handleDurationChange(e.target.value)}
                onBlur={handleDurationBlur}
                className="w-full px-2 py-1.5 text-sm bg-dark-surface border border-dark-border rounded text-dark-text focus:outline-none focus:ring-2 focus:ring-focus focus:border-focus"
              />
            </div>

            {/* Target Rotation Input - Movement Tools Only */}
            {!isControl && (
              <div className="flex flex-col gap-1">
                <label
                  htmlFor={`rotation-${tool.id}`}
                  className="text-xs font-medium text-dark-text-muted"
                >
                  Rotation (deg)
                </label>
                <input
                  id={`rotation-${tool.id}`}
                  type="number"
                  step="1"
                  value={rotationValue}
                  onChange={(e) => handleRotationChange(e.target.value)}
                  onBlur={handleRotationBlur}
                  className="w-full px-2 py-1.5 text-sm bg-dark-surface border border-dark-border rounded text-dark-text focus:outline-none focus:ring-2 focus:ring-focus focus:border-focus"
                />
              </div>
            )}

            {/* Easing Dropdown - Movement Tools Only */}
            {!isControl && (
              <div className="flex flex-col gap-1">
                <label
                  htmlFor={`easing-${tool.id}`}
                  className="text-xs font-medium text-dark-text-muted"
                >
                  Easing
                </label>
                <select
                  id={`easing-${tool.id}`}
                  value={tool.easing}
                  onChange={handleEasingChange}
                  className="w-full px-2 py-1.5 text-sm bg-dark-surface border border-dark-border rounded text-dark-text focus:outline-none focus:ring-2 focus:ring-focus focus:border-focus cursor-pointer"
                >
                  {EASING_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Target Position Dropdown - Reset Position Tool Only */}
            {isResetPosition && (
              <div className="flex flex-col gap-1">
                <label
                  htmlFor={`target-position-${tool.id}`}
                  className="text-xs font-medium text-dark-text-muted"
                >
                  Target Position
                </label>
                <select
                  id={`target-position-${tool.id}`}
                  value={tool.targetPosition ?? 'start'}
                  onChange={handleTargetPositionChange}
                  className="w-full px-2 py-1.5 text-sm bg-dark-surface border border-dark-border rounded text-dark-text focus:outline-none focus:ring-2 focus:ring-focus focus:border-focus cursor-pointer"
                >
                  {TARGET_POSITION_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <Popover.Arrow className="fill-dark-bg" />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
